/**
 This file is used for controlling the global states of the components,
 you can customize the states for the different components here.
 */

import {createContext, useContext, useMemo, useReducer} from "react";
import PropTypes from "prop-types";

const AppUIContext = createContext(null);
AppUIContext.displayName = "AppUIContext";

function reducer(state, action) {
    switch (action.type) {
        case "MINI_SIDENAV": {
            return {...state, miniSidenav: action.value};
        }
        case "DARK_SIDENAV": {
            return {...state, darkSidenav: action.value};
        }
        case "SIDENAV_COLOR": {
            return {...state, sidenavColor: action.value};
        }
        case "TRANSPARENT_NAVBAR": {
            return {...state, transparentNavbar: action.value};
        }
        case "FIXED_NAVBAR": {
            return {...state, fixedNavbar: action.value};
        }
        case "OPEN_CONFIGURATOR": {
            return {...state, openConfigurator: action.value};
        }
        case "DIRECTION": {
            return {...state, direction: action.value};
        }
        case "LAYOUT": {
            return {...state, layout: action.value};
        }
        case "DARK_MODE": {
            return {...state, darkMode: action.value};
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
}

// AppUIContext Dashboard 2 MUI context provider
function AppUIControllerProvider({children}) {
    const initialState = {
        miniSidenav: false,
        darkSidenav: false,
        sidenavColor: null,
        transparentNavbar: true,
        fixedNavbar: false,
        openConfigurator: false,
        direction: "ltr",
        layout: "dashboard",
        darkMode: false,
    };

    const [controller, dispatch] = useReducer(reducer, initialState);

    const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

    return <AppUIContext.Provider value={value}>{children}</AppUIContext.Provider>;
}

// AppUIContext Dashboard 2 MUI custom hook for using context
function useAppUIController() {
    const context = useContext(AppUIContext);

    if (!context) {
        throw new Error("useAppUIController should be used inside the AppUIControllerProvider.");
    }

    return context;
}

// Typechecking props for the AppUIControllerProvider
AppUIControllerProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({type: "MINI_SIDENAV", value});
const setDarkSidenav = (dispatch, value) => dispatch({type: "DARK_SIDENAV", value});
const setSidenavColor = (dispatch, value) => dispatch({type: "SIDENAV_COLOR", value});
const setTransparentNavbar = (dispatch, value) => dispatch({type: "TRANSPARENT_NAVBAR", value});
const setFixedNavbar = (dispatch, value) => dispatch({type: "FIXED_NAVBAR", value});
const setOpenConfigurator = (dispatch, value) => dispatch({type: "OPEN_CONFIGURATOR", value});
const setDirection = (dispatch, value) => dispatch({type: "DIRECTION", value});
const setLayout = (dispatch, value) => dispatch({type: "LAYOUT", value});
const setDarkMode = (dispatch, value) => dispatch({type: "DARK_MODE", value});

export {
    AppUIControllerProvider,
    useAppUIController,
    setMiniSidenav,
    setDarkSidenav,
    setSidenavColor,
    setTransparentNavbar,
    setFixedNavbar,
    setOpenConfigurator,
    setDirection,
    setLayout,
    setDarkMode,
};
